import React, { type FunctionComponent } from 'react'
import { Link } from 'react-router'
import { useIdentity } from '@equipmentshare/auth0-react'
import { type Integration } from '../../graphql/generated/generated'
import {
	appendJDLinkToHeapLocation,
	showIntercom,
	trackAnalyticEvent,
	ANALYTICS_LOCATIONS,
} from '../../utils'
import { IframeLink } from '../'
import { getHelpUrl, T3_HELP_URL_KEY } from '../../utils/urls'
import { AvailableCard, AvailableCardContent, AvailableCardDivider, AvailableCardHeader, AvailableCardTitle, IntegrationImage, StyledAddIcon, StyledButton, StyledChatIcon } from './available-integration.styles'

type AvailableIntegrationProps = {
	availableIntegration: Integration,
}


export const AvailableIntegration: FunctionComponent<AvailableIntegrationProps> = ( { availableIntegration } ) => {
	const { id, selfServiceSupported, name, helpUrls, oemLogoUrl } = availableIntegration
	const { companyName } = useIdentity()
	const learnMoreUrl = getHelpUrl( T3_HELP_URL_KEY, helpUrls )

	return (
		<AvailableCard>
			<AvailableCardHeader
				avatar={ <IntegrationImage src={ oemLogoUrl ?? undefined } alt={ name } /> }
				title={ <AvailableCardTitle variant={ 'h2' } component={ 'h4' }>{ name }</AvailableCardTitle> }
				subheader={
					learnMoreUrl && (
						<IframeLink
							href={ learnMoreUrl }
							target={ '_blank' }
							onClick={ () => {
								trackAnalyticEvent(
									ANALYTICS_LOCATIONS.AVAILABLE_INTEGRATIONS,
									'Click',
									appendJDLinkToHeapLocation( 'Learn More', name ),
									{ integration: `${name} (${id})` },
								)
							} }
						>
							Learn More
						</IframeLink>
					)
				}
			/>
			<AvailableCardDivider />
			<AvailableCardContent>
				{ selfServiceSupported ? (
					<Link
						to={ `create/${id}` }
						onClick={ () => {
							trackAnalyticEvent(
								ANALYTICS_LOCATIONS.AVAILABLE_INTEGRATIONS,
								'Click',
								'Add Integration',
								{
									integration: `${name} (${id})`,
									...( companyName && { companyName } ),
								},
							)
						} }
					>
						<StyledButton variant={ 'outlined' }>
							<StyledAddIcon fontSize="small" />
							Add integration
						</StyledButton>
					</Link>
				) : (
					<StyledButton variant={ 'outlined' } onClick={ () => {
						trackAnalyticEvent(
							ANALYTICS_LOCATIONS.AVAILABLE_INTEGRATIONS,
							'Click',
							'Contact Support',
							{ integration: `${name} (${id})` },
						)
						showIntercom( `Could you help me set up a ${name} integration?` )
					} }>
						<StyledChatIcon fontSize="small" />
						Contact support
					</StyledButton>
				) }
			</AvailableCardContent>
		</AvailableCard>
	)
}
